import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { newsList, type NewsTag } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

const tags: (NewsTag | "全部")[] = ["全部", "影视动态", "行业深度", "明星访谈"];

export default function News() {
  const [tag, setTag] = useState<NewsTag | "全部">("全部");
  const [page, setPage] = useState(1);

  usePageMeta(
    "影视资讯 - 国产久久久",
    "国产久久久影视资讯专题页，提供最新影视行业动态、深度报道与明星访谈，了解国产影视最新风向。",
    "影视资讯,国产影视,行业动态,明星访谈,国产久久久"
  );

  const filtered = useMemo(() => {
    const list =
      tag === "全部" ? newsList : newsList.filter((item) => item.tag === tag);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageList = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            影视资讯
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">
            汇聚国产影视行业的最新动态、深度观察与影人访谈，帮您及时了解国产影视的风向与变化。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {tags.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => {
                setTag(item);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                tag === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:text-primary-600"
              }`}
            >
              {item}
            </button>
          ))}
          <span className="ml-auto text-sm text-foreground-500 whitespace-nowrap">
            共 {filtered.length} 篇
          </span>
        </div>

        {pageList.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pageList.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
            <Pagination
              currentPage={safePage}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        ) : (
          <div className="py-20 text-center rounded-lg bg-background-50 border border-background-200">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
              <i className="ri-article-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950">
              暂无该标签下的资讯
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              换个标签看看，或浏览全部资讯内容。
            </p>
            <button
              type="button"
              onClick={() => setTag("全部")}
              className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              查看全部资讯
            </button>
          </div>
        )}
      </div>
    </div>
  );
}